<script setup lang="ts">
import type { PlanName } from '~/composables/usePlanConfig'

// One page per feature (/features/{slug}). Copy lives in content/8.features;
// the menu label, icon and order come from FEATURE_LINKS. Prerendered (the
// routes are listed in nuxt.config.ts), so queryCollection runs at build time.

const route = useRoute()
const slug = String(route.params.slug)

const { data: page } = await useAsyncData(`feature-${slug}`, () =>
  queryCollection('features').path(route.path).first()
)

if (!page.value) {
  throw createError({ statusCode: 404, statusMessage: 'Page not found', fatal: true })
}

const feature = FEATURE_LINKS.find(f => f.slug === slug)
const label = feature?.label ?? page.value.title
const title = page.value.seo?.title || page.value.title
const description = page.value.seo?.description || page.value.description

useSeoMeta({
  title,
  // seo titles already end with the brand; skip the global "%s - mychar.gg"
  titleTemplate: (t?: string | null) => (t && /mychar\.gg/i.test(t) ? t : `${t} | mychar.gg`),
  ogTitle: title,
  description,
  ogDescription: description
})

defineOgImageComponent('Saas', {
  title: label,
  description: page.value.description
})

const siteUrl = useRuntimeConfig().public.siteUrl
const breadcrumb = [
  { label: 'Home', to: '/' },
  { label: 'Features', to: '/features' },
  { label, to: route.path }
]

// FAQ answers may contain [label](url) links; schema answers get plain text
const stripLinks = (text: string) => text.replace(/\[([^\]]+)\]\([^)\s]+\)/g, '$1')
const faq = computed(() => page.value?.faq ?? [])

useSchemaOrg([
  defineWebPage(faq.value.length ? { '@type': ['WebPage', 'FAQPage'] } : {}),
  defineBreadcrumb({
    itemListElement: breadcrumb.map(item => ({ name: item.label, item: `${siteUrl}${item.to === '/' ? '' : item.to}` }))
  }),
  ...faq.value.map(item => defineQuestion({ name: item.label, acceptedAnswer: stripLinks(item.content) }))
])

const heroLinks = computed(() => [
  { label: 'Create your page', to: '/signup', color: 'primary' as const, trailingIcon: 'i-lucide-arrow-right' },
  ...(page.value?.link ? [{ ...page.value.link, color: 'neutral' as const, variant: 'outline' as const }] : [])
])

const plans = computed(() => {
  const p = page.value?.plans
  if (!p) return []
  return ([['free', p.free], ['pro', p.pro], ['elite', p.elite]] as [PlanName, string][])
    .map(([plan, text]) => ({ plan, text }))
})

const otherFeatures = FEATURE_LINKS.filter(f => f.slug !== slug)
</script>

<template>
  <div v-if="page">
    <UPageHero
      :title="page.title"
      :description="page.description"
      :links="heroLinks"
      :orientation="page.mock ? 'horizontal' : 'vertical'"
      :ui="{
        container: page.mock ? 'lg:gap-16' : '',
        title: page.mock ? 'max-w-xl lg:text-5xl' : '',
        description: page.mock ? 'max-w-xl' : ''
      }"
    >
      <template #headline>
        <UBreadcrumb
          :items="breadcrumb"
          :ui="{ list: page.mock ? '' : 'justify-center' }"
        />
      </template>

      <template
        v-if="page.mock"
        #default
      >
        <HeroPreview v-if="page.mock === 'page'" />
        <ShowcaseDesign v-else-if="page.mock === 'design'" />
        <ShowcaseIntegrations v-else-if="page.mock === 'integrations'" />
        <ShowcaseContent v-else-if="page.mock === 'content'" />
        <ShowcaseAnalytics v-else-if="page.mock === 'analytics'" />
        <ShowcaseCommunity v-else-if="page.mock === 'community'" />
        <ShowcaseNiche v-else-if="page.mock === 'niche'" />
        <ShowcaseDomain v-else-if="page.mock === 'domain'" />
        <LeaderboardTeaser v-else-if="page.mock === 'leaderboard'" />
      </template>
    </UPageHero>

    <UPageSection
      :headline="label"
      title="What you get"
      :features="page.highlights"
    />

    <!-- What each plan includes for this feature -->
    <UContainer
      v-if="plans.length"
      class="pb-8"
    >
      <div class="mx-auto grid max-w-5xl gap-4 md:grid-cols-3">
        <div
          v-for="item in plans"
          :key="item.plan"
          class="rounded-2xl border border-default bg-elevated/40 p-6 shadow-(--surface-shadow)"
        >
          <PlanBadge
            :plan="item.plan"
            size="sm"
          />
          <p class="mt-3 text-sm text-muted">
            {{ item.text }}
          </p>
        </div>
      </div>
      <p class="mt-4 text-center text-sm text-muted">
        Pro and Elite are one-time payments.
        <ULink
          raw
          to="/pricing"
          class="font-medium text-primary underline decoration-primary/30 underline-offset-4 hover:decoration-primary"
        >
          See pricing
        </ULink>
      </p>
    </UContainer>

    <UContainer class="py-8 sm:py-12">
      <div class="mx-auto max-w-3xl">
        <ContentRenderer
          v-if="page.body"
          :value="page"
        />
      </div>
    </UContainer>

    <UPageSection
      v-if="faq.length"
      title="Frequently asked questions"
    >
      <UAccordion
        :items="faq"
        :unmount-on-hide="false"
        type="multiple"
        class="mx-auto flex w-full max-w-3xl flex-col gap-3"
        :ui="{
          item: 'rounded-xl border border-default bg-elevated/40 px-4 border-b-0 last:border-b-0 shadow-(--surface-shadow)',
          trigger: 'text-base text-highlighted',
          body: 'text-base text-muted'
        }"
      >
        <template #body="{ item }">
          <CompareText :text="item.content" />
        </template>
      </UAccordion>
    </UPageSection>

    <!-- Other features -->
    <UContainer class="pb-4">
      <div class="mx-auto flex max-w-5xl flex-col items-center gap-4 text-center">
        <h2 class="text-lg font-semibold text-highlighted">
          More features
        </h2>
        <div class="flex flex-wrap justify-center gap-2">
          <UButton
            v-for="other in otherFeatures"
            :key="other.slug"
            :to="featurePath(other.slug)"
            :label="other.label"
            :icon="other.icon"
            color="neutral"
            variant="outline"
            size="sm"
          />
          <UButton
            to="/features"
            label="All features"
            color="neutral"
            variant="ghost"
            size="sm"
            trailing-icon="i-lucide-arrow-right"
          />
        </div>
      </div>
    </UContainer>

    <CtaPanel />
  </div>
</template>
